import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateDE } from "@/lib/playerConstants";
import { ArrowLeft, Pencil, Clock, Users, X } from "lucide-react";
import { toast } from "sonner";

const BLOCK_LABELS = {
  aktivierung: "Aktivierung / Warm-Up",
  spielform_1: "Spielform 1",
  zwischenblock: "Zwischenblock / Technik",
  spielform_2: "Spielform 2",
};
const BLOCK_ORDER = ["aktivierung", "spielform_1", "zwischenblock", "spielform_2"];

export default function TrainingSessionView({ publicMode = false }) {
  const params = useParams();
  const trainingId = params.id;
  const shareToken = params.token;
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);      // {datum, uhrzeit, ort, art}
  const [session, setSession] = useState(null);        // {name, focus_area_ids?/focus_names, blocks}
  const [focusNamesById, setFocusNamesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // src

  useEffect(() => {
    (async () => {
      try {
        if (publicMode) {
          const { data } = await api.get(`/training-sessions/public/${shareToken}`);
          setSession({
            name: data.name,
            blocks: data.blocks || [],
            focus_names: data.focus_names || [],
          });
          setTraining(data.training || null);
        } else {
          const [tRes, sRes, faRes] = await Promise.all([
            api.get(`/trainings/${trainingId}`),
            api.get(`/trainings/${trainingId}/session`),
            api.get("/focus-areas"),
          ]);
          setTraining(tRes.data);
          setSession(sRes.data);
          setFocusNamesById(Object.fromEntries((faRes.data || []).map((f) => [f.id, f.name])));
          if (!sRes.data) {
            navigate(`/training/${trainingId}/einheit/bearbeiten`, { replace: true });
          }
        }
      } catch {
        toast.error("Trainingseinheit nicht gefunden");
        if (!publicMode) navigate(`/training/${trainingId}`);
      } finally { setLoading(false); }
    })();
  }, [trainingId, shareToken, publicMode, navigate]);

  if (loading || !session) return <div className="max-w-2xl mx-auto p-6 text-zinc-500 text-sm">Lädt…</div>;

  const blockMap = Object.fromEntries((session.blocks || []).map((b) => [b.key, b]));

  // Resolve focus labels: authenticated mode uses id→name map, public mode has ready names
  const focusLabels = publicMode
    ? (session.focus_names || [])
    : (session.focus_area_ids || []).map((id) => focusNamesById[id] || "Unbekannt");

  const hasContent = (b) => b && (b.uebung || b.dauer_min || b.spieler_von != null ||
    b.organisation || b.material || b.ablauf || b.variationen || b.coaching || b.tipps ||
    (b.fotos || []).length > 0);

  return (
    <div className="min-h-screen bg-zinc-950" data-testid={publicMode ? "ts-view-public" : "ts-view"}>
      {/* Kompakte Sticky-Leiste – Vollbreite auf Mobile, zentriert auf Desktop */}
      <div className="sticky top-0 z-30 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-2xl lg:max-w-6xl xl:max-w-[1400px] mx-auto flex items-center gap-2 px-3 sm:px-6 lg:px-8 h-12">
          {!publicMode && (
            <button
              onClick={() => navigate(`/training/${trainingId}`)}
              className="h-9 w-9 -ml-1 rounded-md text-zinc-300 hover:text-brand-lighter hover:bg-zinc-900 flex items-center justify-center transition"
              data-testid="tsv-back"
              aria-label="Zurück"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          {publicMode && (
            <div className="h-9 w-9 -ml-1 rounded-md bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4"><path d="M4 20h16M4 20l4-8 4 6 4-10 4 12" /></svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-emerald-400 leading-none">Trainingseinheit</div>
            <div className="text-sm font-semibold text-zinc-100 truncate leading-tight" data-testid="tsv-title-compact">
              {session.name || "Ohne Bezeichnung"}
            </div>
          </div>
          <button
            onClick={() => navigate(`/training/${trainingId}/einheit/bearbeiten`)}
            className={
              "h-9 min-w-[36px] px-2 sm:px-3 rounded-md text-zinc-200 hover:text-brand-lighter hover:bg-zinc-900 inline-flex items-center gap-1.5 transition " +
              (publicMode ? "hidden" : "")
            }
            data-testid="tsv-edit-btn"
            aria-label="Bearbeiten"
          >
            <Pencil className="h-4 w-4" />
            <span className="hidden sm:inline text-sm">Bearbeiten</span>
          </button>
        </div>
      </div>

      {/* Inhalt: Full-Width auf Mobile, zentriert auf Desktop */}
      <div className="max-w-2xl lg:max-w-6xl xl:max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-24 sm:pb-10">
        {/* Kontext-Zeile (Datum · Uhrzeit · Ort) – dezent */}
        {training && (
          <div className="text-xs text-zinc-400 mb-4" data-testid="tsv-context">
            {formatDateDE(training.datum)} · {training.uhrzeit}{training.ort ? " · " + training.ort : ""}
          </div>
        )}

      {/* Schwerpunkte */}
      {focusLabels.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2" data-testid="tsv-focus">
          {focusLabels.map((name, i) => (
            <span key={i} className="px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-medium">
              {name}
            </span>
          ))}
        </div>
      )}

      {/* 4 Blöcke */}
      <div className="space-y-4 sm:space-y-5">
        {BLOCK_ORDER.map((key, i) => {
          const b = blockMap[key];
          if (!hasContent(b)) return (
            <div key={key} className="rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 px-4 py-3 text-xs text-zinc-500" data-testid={`tsv-block-empty-${i}`}>
              <span className="font-display text-base font-bold text-zinc-600 mr-2">{String(i + 1).padStart(2, "0")}</span>
              {BLOCK_LABELS[key]} · leer
            </div>
          );
          return (
            <div key={key} className="rounded-lg border border-zinc-800 bg-zinc-900/60 overflow-hidden" data-testid={`tsv-block-${i}`}>
              {/* Block-Header */}
              <div className="px-4 py-3 border-b border-emerald-500/20 bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-transparent">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-2xl font-bold text-emerald-400 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-400/80 font-semibold">{BLOCK_LABELS[key]}</div>
                    <div className="text-lg font-semibold text-zinc-50 break-words">{b.uebung || "—"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-zinc-300">
                  {b.dauer_min != null && (
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-emerald-400" /> {b.dauer_min} min</span>
                  )}
                  {(b.spieler_von != null || b.spieler_bis != null) && (
                    <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-emerald-400" />
                      {b.spieler_von ?? "?"}{b.spieler_bis != null && b.spieler_bis !== b.spieler_von ? `–${b.spieler_bis}` : ""} Sp.
                    </span>
                  )}
                </div>
              </div>

              {/* Body: auf Desktop Bild links, Texte rechts – Platz sinnvoll verteilen */}
              <div className="lg:flex lg:gap-6 p-3 sm:p-4">
                {(b.fotos || []).length > 0 && (
                  <div className="mb-3 lg:mb-0 lg:w-[42%] lg:shrink-0">
                    <button type="button" onClick={() => setLightbox(b.fotos[0])}
                      className="block w-full rounded-md overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-emerald-400/50 transition lg:sticky lg:top-16"
                      data-testid={`tsv-b${i}-photo-0`}>
                      <img src={b.fotos[0]} alt="" className="w-full h-auto object-contain" />
                    </button>
                  </div>
                )}
                <div className={
                  "flex-1 min-w-0 space-y-3 " +
                  ((b.fotos || []).length === 0 ? "lg:grid lg:grid-cols-2 lg:gap-x-6 lg:gap-y-3 lg:space-y-0" : "")
                }>
                  <ViewSection label="Organisation" text={b.organisation} />
                  <ViewSection label="Material" text={b.material} />
                  <ViewSection label="Ablauf" text={b.ablauf} highlight />
                  <ViewSection label="Variationen" text={b.variationen} />
                  <ViewSection label="Coachingpunkte" text={b.coaching} />
                  <ViewSection label="Tipps / Korrekturen" text={b.tipps} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)} data-testid="tsv-lightbox">
          <img src={lightbox} alt="" className="max-h-full max-w-full object-contain" />
          <button onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}

function ViewSection({ label, text, highlight }) {
  if (!text) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-emerald-400/80 font-semibold mb-1.5">{label}</div>
      <div
        className={
          "rounded-md px-3 py-2.5 text-sm leading-relaxed whitespace-pre-wrap border " +
          (highlight
            ? "bg-emerald-500/10 border-emerald-500/30 text-zinc-50"
            : "bg-zinc-800/60 border-zinc-700/60 text-zinc-100")
        }
      >
        {text}
      </div>
    </div>
  );
}
